import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  BarChart3, 
  Search, 
  Download, 
  Calendar, 
  User, 
  Filter,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertTriangle,
  FileText,
  Users,
  Target,
  Activity,
  RefreshCw,
  AlertCircle,
  Loader2
} from 'lucide-react';
import toast from 'react-hot-toast';
import StaffReportService, { ValidationError, StaffReportError } from '../../services/StaffReportService';
import StaffReportDetail from './StaffReportDetail';

const StaffReports = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [showReportDetail, setShowReportDetail] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [filters, setFilters] = useState({
    staff_level: '',
    is_active: true,
    category_id: ''
  });

  // Date range state with validation
  const [dateRange, setDateRange] = useState({
    date_from: '',
    date_to: ''
  });

  // Validation state
  const [validationErrors, setValidationErrors] = useState({});
  const [isExporting, setIsExporting] = useState(false);

  // Validate parameters in real-time
  const paramValidation = useMemo(() => {
    const params = { ...dateRange, ...filters };
    
    // Use the enhanced validator from service
    if (StaffReportService.StaffReportValidator?.validateReportParams) {
      return StaffReportService.StaffReportValidator.validateReportParams(params);
    }
    
    // Fallback basic validation
    const errors = {};
    const warnings = [];
    
    if (dateRange.date_from && dateRange.date_to) {
      const fromDate = new Date(dateRange.date_from);
      const toDate = new Date(dateRange.date_to);
      
      if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
        errors.dateFormat = 'Invalid date format. Use YYYY-MM-DD';
      } else {
        if (fromDate > toDate) {
          errors.dateRange = 'From date must be before to date';
        }
        
        if (toDate > new Date()) {
          errors.dateTo = 'To date cannot be in the future';
        }

        const daysDiff = (toDate - fromDate) / (1000 * 60 * 60 * 24);
        if (daysDiff > 365) {
          errors.dateRange = 'Date range cannot exceed 365 days';
        }
      }
    }

    // Staff level validation
    if (params.staff_level !== undefined && params.staff_level !== '') {
      const level = Number(params.staff_level);
      if (isNaN(level) || ![1, 2, 3].includes(level)) {
        errors.staff_level = 'Invalid staff level. Must be 1, 2, or 3';
      }
    }
    
    return { isValid: Object.keys(errors).length === 0, errors, warnings };
  }, [dateRange, filters]);

  // Update validation errors when parameters change
  useEffect(() => {
    setValidationErrors(paramValidation.errors || {});
  }, [paramValidation]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportDropdown && !event.target.closest('.export-dropdown-container')) {
        setShowExportDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportDropdown]);

  // Get report summary with enhanced error handling
  const { 
    data: summaryData, 
    isLoading: summaryLoading, 
    error: summaryError,
    refetch: refetchSummary 
  } = useQuery({
    queryKey: ['staff-report-summary', dateRange],
    queryFn: async () => {
      try {
        const response = await StaffReportService.getReportSummary(dateRange);
        return response;
      } catch (error) {
        if (error instanceof ValidationError) {
          throw new Error(`Invalid parameters: ${Object.values(error.context?.errors || {}).join(', ')}`);
        }
        if (error instanceof StaffReportError) {
          throw new Error(`Report error: ${error.message}`);
        }
        throw error;
      }
    },
    enabled: paramValidation.isValid,
    retry: (failureCount, error) => {
      // Don't retry validation errors
      if (error.message.includes('Invalid parameters') || error.message.includes('Report error:')) {
        return false;
      }
      return failureCount < 2;
    },
    staleTime: 60000, // Cache for 1 minute
    gcTime: 300000 // Keep in cache for 5 minutes
  });

  // Search staff with enhanced error handling
  const { 
    data: staffResults, 
    isLoading: searchLoading, 
    error: searchError,
    refetch: refetchStaff 
  } = useQuery({
    queryKey: ['staff-search', searchTerm, filters, dateRange],
    queryFn: async () => {
      try {
        const searchParams = {
          search: searchTerm || undefined,
          ...filters,
          ...dateRange,
          limit: 20
        };
        
        // Remove empty values
        Object.keys(searchParams).forEach(key => {
          if (searchParams[key] === '' || searchParams[key] === null || searchParams[key] === undefined) {
            delete searchParams[key];
          }
        });

        return await StaffReportService.searchStaff(searchParams);
      } catch (error) {
        if (error instanceof ValidationError) {
          throw new Error(`Search failed: ${Object.values(error.context?.errors || {}).join(', ')}`);
        }
        if (error instanceof StaffReportError) {
          throw new Error(`Search error: ${error.message}`);
        }
        throw error;
      }
    },
    enabled: paramValidation.isValid,
    retry: (failureCount, error) => {
      if (error.message.includes('Search failed:') || error.message.includes('Search error:')) {
        return false;
      }
      return failureCount < 2;
    },
    staleTime: 30000, // Cache for 30 seconds
    gcTime: 300000
  });

  // Format data safely using enhanced formatters
  const summary = useMemo(() => {
    if (!summaryData?.data) return null;
    try {
      if (StaffReportService.StaffReportFormatter?.formatSummaryData) {
        return StaffReportService.StaffReportFormatter.formatSummaryData(summaryData);
      }
      return summaryData.data;
    } catch (error) {
      console.error('Error formatting summary data:', error);
      return summaryData.data || null;
    }
  }, [summaryData]);

  const staffList = useMemo(() => {
    if (!staffResults?.data) return [];
    try {
      if (StaffReportService.StaffReportFormatter?.formatSearchResults) {
        return StaffReportService.StaffReportFormatter.formatSearchResults(staffResults);
      }
      return staffResults.data.staff || [];
    } catch (error) {
      console.error('Error formatting staff results:', error);
      return staffResults.data.staff || [];
    }
  }, [staffResults]);

  // Handle search with debouncing
  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  // Handle filter change with validation
  const handleFilterChange = (key, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };
      
      // Clear validation errors for this field
      if (validationErrors[key]) {
        setValidationErrors(prev => {
          const { [key]: removed, ...rest } = prev;
          return rest;
        });
      }
      
      return newFilters;
    });
  };

  // Handle date range changes with validation
  const handleDateRangeChange = (key, value) => {
    setDateRange(prev => {
      const newRange = { ...prev, [key]: value };
      
      // Clear validation errors for date fields
      if (validationErrors.dateRange || validationErrors[key]) {
        setValidationErrors(prev => {
          const { dateRange, date_from, date_to, ...rest } = prev;
          return rest;
        });
      }
      
      return newRange;
    });
  };

  // Handle date range preset
  const handleDateRangePreset = (preset) => {
    setDateRange({
      date_from: preset.dateFrom,
      date_to: preset.dateTo
    });
    // Clear any date-related validation errors
    setValidationErrors(prev => {
      const { dateRange, date_from, date_to, ...rest } = prev;
      return rest;
    });
  };

  // View staff report
  const handleViewReport = (staff) => {
    setSelectedStaff(staff);
    setShowReportDetail(true);
  };

  // Close report detail
  const handleCloseReport = () => {
    setShowReportDetail(false);
    setSelectedStaff(null);
  };

  // Export system report with enhanced error handling
  const handleExportSystemReport = async (format) => {
    if (!paramValidation.isValid) {
      toast.error('Please fix validation errors before exporting');
      return;
    }

    setIsExporting(true);
    try {
      const exportParams = {
        ...dateRange,
        format
      };

      // Remove empty values
      Object.keys(exportParams).forEach(key => {
        if (exportParams[key] === '' || exportParams[key] === null || exportParams[key] === undefined) {
          delete exportParams[key];
        }
      });

      await StaffReportService.exportSystemReport(exportParams);
      toast.success(`System report exported as ${format.toUpperCase()}`);
      setShowExportDropdown(false);
    } catch (error) {
      console.error('Export failed:', error);
      
      if (error instanceof ValidationError) {
        toast.error(`Export failed: ${Object.values(error.context?.errors || {}).join(', ')}`);
      } else if (error instanceof StaffReportError) {
        toast.error(`Export failed: ${error.message}`);
      } else {
        toast.error('Failed to export system report. Please try again.');
      }
    } finally {
      setIsExporting(false);
    }
  };

  // Get staff level badge color
  const getStaffLevelBadgeColor = (level) => {
    const colors = {
      1: 'bg-green-100 text-green-800',
      2: 'bg-blue-100 text-blue-800',
      3: 'bg-purple-100 text-purple-800'
    };
    return colors[level] || 'bg-gray-100 text-gray-800';
  };

  // Format number with commas
  const formatNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '0';
    return new Intl.NumberFormat().format(num);
  };

  // Format percentage safely
  const formatPercentage = (value, decimals = 1) => {
    if (value === null || value === undefined || isNaN(value)) return '0.0%';
    return `${Number(value).toFixed(decimals)}%`;
  };

  // Format duration using service formatter if available
  const formatDuration = (minutes) => {
    if (StaffReportService.StaffReportFormatter?.formatDuration) {
      return StaffReportService.StaffReportFormatter.formatDuration(minutes);
    }
    
    if (!minutes || minutes === 0) return '0 min';
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = Math.round(minutes % 60);
    
    if (hours === 0) return `${remainingMinutes} min`;
    if (remainingMinutes === 0) return `${hours}h`;
    return `${hours}h ${remainingMinutes}m`;
  };

  // Get available export formats - Updated to use CSV instead of Excel
  const exportFormats = StaffReportService.getAvailableFormats ? 
    StaffReportService.getAvailableFormats() : 
    [
      { value: 'pdf', label: 'PDF Document', icon: '📄' },
      { value: 'csv', label: 'CSV Spreadsheet', icon: '📊' } // Changed from excel to csv
    ];

  // Get staff level options
  const staffLevelOptions = StaffReportService.getStaffLevelOptions ? 
    StaffReportService.getStaffLevelOptions() : 
    [
      { value: '', label: 'All Levels' },
      { value: 1, label: 'Level 1 - Junior Staff' },
      { value: 2, label: 'Level 2 - Senior Staff' },
      { value: 3, label: 'Level 3 - Lead Staff' }
    ];

  // Get date range presets
  const dateRangePresets = StaffReportService.getDateRangePresets ? 
    StaffReportService.getDateRangePresets() : [];

  if (showReportDetail && selectedStaff) {
    return (
      <StaffReportDetail
        staff={selectedStaff}
        dateRange={dateRange}
        onClose={handleCloseReport}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <BarChart3 className="w-6 h-6 mr-2" />
            Staff Reports
          </h1>
          <p className="text-gray-600 mt-1">View performance reports and analytics for staff members</p>
        </div>
        
        <div className="flex gap-3">
          {/* Refresh Button */}
          <button
            onClick={() => {
              refetchSummary();
              refetchStaff();
            }}
            className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
            disabled={summaryLoading || searchLoading}
          >
            <RefreshCw className={`w-4 h-4 ${(summaryLoading || searchLoading) ? 'animate-spin' : ''}`} />
            Refresh
          </button>

          {/* Export Dropdown */}
          <div className="relative export-dropdown-container">
            <button 
              onClick={() => setShowExportDropdown(!showExportDropdown)}
              className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 disabled:opacity-50"
              disabled={!paramValidation.isValid || isExporting}
            >
              {isExporting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Export System Report
            </button>
            {showExportDropdown && (
              <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-10">
                <div className="py-1">
                  {exportFormats.map((format) => (
                    <button
                      key={format.value}
                      onClick={() => handleExportSystemReport(format.value)}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      disabled={isExporting}
                    >
                      <span>{format.icon}</span>
                      {format.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Validation Errors */}
      {Object.keys(validationErrors).length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <h3 className="text-sm font-medium text-red-800">Please fix the following errors:</h3>
          </div>
          <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
            {Object.entries(validationErrors).map(([field, error]) => (
              <li key={field}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Validation Warnings */}
      {paramValidation.warnings && paramValidation.warnings.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2" />
            <h3 className="text-sm font-medium text-yellow-800">Performance Warnings:</h3>
          </div>
          <ul className="mt-2 text-sm text-yellow-700 list-disc list-inside">
            {paramValidation.warnings.map((warning, index) => (
              <li key={index}>{warning}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Date Range and Quick Stats */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Date Range Controls */}
          <div className="flex-1">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Report Period</h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
                <input
                  type="date"
                  value={dateRange.date_from}
                  onChange={(e) => handleDateRangeChange('date_from', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    validationErrors.date_from || validationErrors.dateRange ? 
                    'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
                <input
                  type="date"
                  value={dateRange.date_to}
                  onChange={(e) => handleDateRangeChange('date_to', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    validationErrors.date_to || validationErrors.dateRange ? 
                    'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
              </div>
            </div>
            
            {/* Date Presets */}
            {dateRangePresets.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {dateRangePresets.map((preset, index) => (
                  <button
                    key={index}
                    onClick={() => handleDateRangePreset(preset)}
                    className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Quick Stats */}
          {summaryLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : summaryError ? (
            <div className="flex-1 bg-red-50 rounded-lg p-4 flex items-center">
              <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
              <div>
                <span className="text-sm text-red-700 block">Failed to load summary data</span>
                <span className="text-xs text-red-600">{summaryError.message}</span>
              </div>
            </div>
          ) : summary ? (
            <div className="flex-1">
              <h3 className="text-lg font-medium text-gray-900 mb-4">System Overview</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <Users className="w-5 h-5 text-blue-600 mr-2" />
                    <span className="text-sm font-medium text-blue-900">Active Staff</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-900 mt-1">
                    {formatNumber(summary.overview?.total_active_staff)}
                  </p>
                </div>

                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <FileText className="w-5 h-5 text-green-600 mr-2" />
                    <span className="text-sm font-medium text-green-900">Total Tickets</span>
                  </div>
                  <p className="text-2xl font-bold text-green-900 mt-1">
                    {formatNumber(summary.overview?.total_tickets_in_period)}
                  </p>
                </div>

                <div className="bg-yellow-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <Activity className="w-5 h-5 text-yellow-600 mr-2" />
                    <span className="text-sm font-medium text-yellow-900">Staff Actions</span>
                  </div>
                  <p className="text-2xl font-bold text-yellow-900 mt-1">
                    {formatNumber(summary.overview?.total_actions_in_period)}
                  </p>
                </div>

                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <Target className="w-5 h-5 text-purple-600 mr-2" />
                    <span className="text-sm font-medium text-purple-900">Resolution Rate</span>
                  </div>
                  <p className="text-2xl font-bold text-purple-900 mt-1">
                    {formatPercentage(summary.systemHealth?.tickets_resolved_rate)}
                  </p>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Staff Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Find Staff Member</h3>
        
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          {/* Search Input */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, or ID number..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-4">
            <select
              value={filters.staff_level}
              onChange={(e) => handleFilterChange('staff_level', e.target.value)}
              className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                validationErrors.staff_level ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
            >
              {staffLevelOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <select
              value={filters.is_active}
              onChange={(e) => handleFilterChange('is_active', e.target.value === 'true')}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={true}>Active Only</option>
              <option value={false}>Inactive Only</option>
              <option value="">All Status</option>
            </select>
          </div>
        </div>

        {/* Search Error */}
        {searchError && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-sm text-red-700">{searchError.message}</span>
          </div>
        )}

        {/* Staff Results */}
        <div className="space-y-4">
          {searchLoading ? (
            <div className="text-center py-8">
              <div className="inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm shadow rounded-md text-blue-500 bg-blue-100">
                <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500" />
                Searching staff members...
              </div>
            </div>
          ) : staffList.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <User className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No staff members found matching your search criteria.</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {staffList.map((staff) => (
                <div
                  key={staff.id}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => handleViewReport(staff)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="bg-blue-100 rounded-full p-2">
                        <User className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 flex items-center gap-2">
                          {staff.name}
                          <span className={`text-xs px-2 py-1 rounded-full ${getStaffLevelBadgeColor(staff.staffLevel)}`}>
                            {staff.staffLevelName}
                          </span>
                          {!staff.isActive && (
                            <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-800">
                              Inactive
                            </span>
                          )}
                        </h4>
                        <p className="text-sm text-gray-500">{staff.email}</p>
                        {staff.userIdNumber && (
                          <p className="text-xs text-gray-400">ID: {staff.userIdNumber}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-6 text-sm">
                      <div className="text-center">
                        <div className="font-medium text-gray-900">
                          {staff.enhancedStats?.lifecycle_metrics?.total_assigned || 0}
                        </div>
                        <div className="text-gray-500">Total Assigned</div>
                      </div>
                      <div className="text-center">
                        <div className="font-medium text-green-600">
                          {staff.enhancedStats?.lifecycle_metrics?.resolved || 0}
                        </div>
                        <div className="text-gray-500">Resolved</div>
                      </div>
                      <div className="text-center">
                        <div className="font-medium text-blue-600">
                          {staff.enhancedStats?.lifecycle_metrics?.closed || 0}
                        </div>
                        <div className="text-gray-500">Closed</div>
                      </div>
                      <div className="text-center">
                        <div className="font-medium text-yellow-600">
                          {staff.enhancedStats?.lifecycle_metrics?.in_progress || 0}
                        </div>
                        <div className="text-gray-500">In Progress</div>
                      </div>
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewReport(staff);
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                      >
                        <BarChart3 className="w-4 h-4" />
                        View Report
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Top Performers */}
      {summary?.topPerformers && summary.topPerformers.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <TrendingUp className="w-5 h-5 mr-2 text-green-600" />
            Top Performers This Period
          </h3>
          
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {summary.topPerformers.map((performer, index) => (
              <div key={performer.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      index === 0 ? 'bg-yellow-100 text-yellow-800' :
                      index === 1 ? 'bg-gray-100 text-gray-800' :
                      index === 2 ? 'bg-orange-100 text-orange-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="ml-3">
                      <h4 className="font-medium text-gray-900">{performer.name}</h4>
                      <p className="text-sm text-gray-500">{performer.staff_level_name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-green-600">{performer.resolved_tickets}</div>
                    <div className="text-sm text-gray-500">Resolved</div>
                  </div>
                </div>
                <div className="text-sm text-gray-600">
                  {formatPercentage(performer.completion_rate)} completion rate
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* System Health */}
      {summary?.systemHealth && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Activity className="w-5 h-5 mr-2 text-blue-600" />
            System Health Metrics
          </h3>
          
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Resolution Rate</span>
                <span className="font-bold text-green-600">
                  {formatPercentage(summary.systemHealth.tickets_resolved_rate)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-500 h-2 rounded-full" 
                  style={{ width: `${Math.min(summary.systemHealth.tickets_resolved_rate || 0, 100)}%` }}
                ></div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Avg Resolution Time</span>
                <span className="font-bold text-blue-600">
                  {summary.systemHealth.avg_resolution_time_hours}h
                </span>
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <Clock className="w-4 h-4 mr-1" />
                System average for the selected period
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Total Escalations</span>
                <span className="font-medium text-orange-600">
                  {formatNumber(summary.systemHealth.total_escalations)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Total Forwards</span>
                <span className="font-medium text-purple-600">
                  {formatNumber(summary.systemHealth.total_forwards)}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Daily Ticket Average</span>
                <span className="font-medium text-indigo-600">
                  {formatNumber(summary.overview?.avg_tickets_per_day)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Actions per Staff</span>
                <span className="font-medium text-teal-600">
                  {formatNumber(summary.overview?.avg_actions_per_staff)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Staff Breakdown by Level */}
      {summary?.staffBreakdown && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Users className="w-5 h-5 mr-2 text-gray-600" />
            Staff Distribution
          </h3>
          
          <div className="grid gap-4 md:grid-cols-4">
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-900">
                  {formatNumber(summary.staffBreakdown.level_1)}
                </div>
                <div className="text-sm font-medium text-green-700">Level 1 Staff</div>
                <div className="text-xs text-green-600">Junior Staff</div>
              </div>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-900">
                  {formatNumber(summary.staffBreakdown.level_2)}
                </div>
                <div className="text-sm font-medium text-blue-700">Level 2 Staff</div>
                <div className="text-xs text-blue-600">Senior Staff</div>
              </div>
            </div>
            
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-900">
                  {formatNumber(summary.staffBreakdown.level_3)}
                </div>
                <div className="text-sm font-medium text-purple-700">Level 3 Staff</div>
                <div className="text-xs text-purple-600">Lead Staff</div>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {formatNumber(summary.staffBreakdown.total_with_expertise)}
                </div>
                <div className="text-sm font-medium text-gray-700">With Expertise</div>
                <div className="text-xs text-gray-600">Specialized Staff</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffReports;